import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Images, Expand } from "lucide-react";
import { endpoints } from "../../lib/api";
import { Lightbox } from "./Lightbox";

const SPAN = ["col-span-2 row-span-2", "", "", "col-span-2", "", "", "row-span-2", ""];
const PREVIEW = 8;

export function WrapUpGallery() {
  const [photos, setPhotos] = React.useState([]);
  const [open, setOpen] = React.useState(null);
  const [all, setAll] = React.useState(false);

  React.useEffect(() => { endpoints.gallery().then((d) => setPhotos(d.photos)).catch(() => {}); }, []);
  if (!photos.length) return null;

  const shown = all ? photos : photos.slice(0, PREVIEW);
  return (
    <section className="relative mx-auto w-full max-w-6xl px-4 py-14 md:py-20" data-testid="wrapup-gallery">
      <p className="flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.3em] text-sky-500"><Images size={14} /> Hackathon wrap-up</p>
      <h2 className="mt-2 font-display text-[40px] font-extrabold uppercase leading-[0.9] text-white sm:text-[56px]">The weekend in photos</h2>
      <p className="mt-3 max-w-xl text-[15px] text-on-dark-muted">Late nights, big demos and a lot of coffee. Tap any photo to see it full screen.</p>
      <div className="mt-8 grid grid-flow-row-dense auto-rows-[120px] grid-cols-2 gap-2 sm:auto-rows-[160px] md:grid-cols-4 md:gap-3">
        {shown.map((p, i) => (
          <motion.button key={p.id} onClick={() => setOpen(i)} data-testid={`gallery-thumb-${i}`}
            initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: (i % PREVIEW) * 0.05 }}
            className={`group relative overflow-hidden rounded-2xl ring-1 ring-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 ${SPAN[i % SPAN.length]}`}
            aria-label={p.caption ? `Open photo: ${p.caption}` : `Open photo ${i + 1}`}>
            <img src={p.url} alt={p.caption || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <span className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <Expand size={18} className="absolute bottom-3 right-3 text-white opacity-0 transition-opacity group-hover:opacity-100" />
          </motion.button>
        ))}
      </div>
      {photos.length > PREVIEW && (
        <div className="mt-6 text-center">
          <button onClick={() => setAll((v) => !v)} data-testid="gallery-toggle-all"
            className="rounded-pill border border-white/20 px-5 py-2.5 font-semibold text-white hover:bg-white/10">
            {all ? "Show fewer" : `See all ${photos.length} photos`}
          </button>
        </div>
      )}
      <AnimatePresence>
        {open !== null && <Lightbox photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}
